import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import PlanningHistoryDrawer from "./PlanningHistoryDrawer";
import {
  Container, Box, Card, CardContent, Grid, Stack, Avatar, Typography, Chip, Autocomplete,
  FormControl, InputLabel, Select, MenuItem,
  ToggleButton, ToggleButtonGroup,
  Collapse, Fade,
  Button, IconButton, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Snackbar, Alert, LinearProgress, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Link as MuiLink
} from "@mui/material";
// Importing lucide icons for a consistent outline style

import {
  Calendar, Factory, Upload as UploadIcon, FileText, Database,
  ChevronDown, ChevronUp, Home, ChevronRight,
  Shield, TrendingUp, Copy as CopyIcon, Trash2, Plus, X as XIcon, Info as LucideInfo, Eye, Info, CheckCircle, AlertTriangle
} from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

export default function UploadPlan() {
  // State variables
  const [machineGroups, setMachineGroups] = useState([]);
  const [machines, setMachines] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedMachine, setSelectedMachine] = useState("");
  const [viewMode, setViewMode] = useState("plan");  // "plan" or "lineup"

  const [existingPlanData, setExistingPlanData] = useState([]);
  const [historyJobNumber, setHistoryJobNumber] = useState(null);
  const [historySearch, setHistorySearch] = useState("");
  const [jobSuggestions, setJobSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState("");
  const [existingLineupData, setExistingLineupData] = useState([]);
  const [expandedPlanTable, setExpandedPlanTable] = useState(false);
  const [expandedLineupTable, setExpandedLineupTable] = useState(false);

  const [uploadNewplanButtonVisible, setUploadNewplanButtonVisible] = useState(false);
  const [uploadNewLineupButtonVisible, setUploadNewLineupButtonVisible] = useState(false);

  const [pastingBlockVisible, setPastingBlockVisible] = useState(false);
  const [lineupPasteBlockVisible, setLineupPasteBlockVisible] = useState(false);

  const [excelData, setExcelData] = useState("");
  const [lineupExcelData, setLineupExcelData] = useState("");
  const [data, setData] = useState(null);  // Processed table data for preview (array of arrays)
  const [lineupData, setLineupData] = useState(null);  // Processed lineup data for preview
  const [dataProcessing, setDataProcessing] = useState(false);
  const [lineupDataProcessing, setLineupDataProcessing] = useState(false);

  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [planConfirmOpen, setPlanConfirmOpen] = useState(false);
  const [lineupConfirmOpen, setLineupConfirmOpen] = useState(false);

  // Validation error states
  const [validationErrors, setValidationErrors] = useState([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  // Data summary states
  const [dataSummary, setDataSummary] = useState(null);
  const [lineupDataSummary, setLineupDataSummary] = useState(null);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [summaryType, setSummaryType] = useState("plan"); // "plan" or "lineup"

  // Step definitions for the stepper display
  const steps = [
    { label: "Select Time Slot", icon: Calendar, description: "Choose your planning period" },
    { label: "Choose Machine", icon: Factory, description: "Select machine group and machine" },
    { label: "Manage Plan", icon: UploadIcon, description: "View existing plans or upload new data" }
  ];

  // ==================== HELPER FUNCTIONS ====================

  // Helper: format date range for a given slot number (0-indexed)
  const getSlotTimeRange = (slotIndex) => {
    const start = dayjs().add(slotIndex, "day").startOf("day").add(8, "hour");
    const end = start.add(1, "day");
    return { startDate: start, endDate: end };
  };

  // Helper: adjust DateTime for API (adds +5:30h and returns formatted string "YYYY-MM-DD HH:mm:ss")
  const adjustDateTime = (dateTimeVal) => {
    const dateTime = new Date(dateTimeVal);
    dateTime.setHours(dateTime.getHours() + 5);
    dateTime.setMinutes(dateTime.getMinutes() + 30);
    return dateTime.toISOString().slice(0, 19).replace('T', ' ');
  };

  // Snackbar alert helper
  const showAlert = (message, severity = "info") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setOpenSnackbar(true);
  };
  
  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  useEffect(() => {
    const q = historySearch.trim();
    if (q.length < 3 || !/^[a-z0-9-]+$/i.test(q)) {
      setJobSuggestions([]);
      setSuggestionsError("");
      setSuggestionsLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setSuggestionsLoading(true);
    setSuggestionsError("");
    const timer = setTimeout(() => {
      axios.get(`${API_BASE_URL}/api/planning-history/suggestions`, {
        params: { q }, signal: controller.signal
      }).then(({ data }) => setJobSuggestions(data.suggestions || []))
        .catch(error => {
          if (error.code !== "ERR_CANCELED") {
            setSuggestionsError("Suggestions are unavailable. You can still open an exact job number.");
            setJobSuggestions([]);
          }
        })
        .finally(() => { if (!controller.signal.aborted) setSuggestionsLoading(false); });
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [historySearch]);

  // ==================== DATA ANALYSIS HELPER ====================

  const analyzeData = (processedData, type = "plan") => {
    if (!processedData || processedData.length <= 1) return null;
    
    const [headers, ...rows] = processedData;
    const summary = {
      totalRows: rows.length,
      validRows: rows.length,
      emptyRows: 0,
      warnings: [],
      info: [],
      fieldAnalysis: {},
      duplicates: [],
      missingData: {},
      numericStats: {},
      emptyNumericFields: {}, // NEW: Track empty numeric fields
    };

    // Track missing data per field
    const missingCount = {};
    const numericData = {};
    const emptyNumericCount = {}; // NEW
    const jobNumbers = [];
    const customers = [];
    const priorities = {};
    
    rows.forEach((row, rowIndex) => {
      const rowNumber = rowIndex + 2;
      
      // Collect job numbers for duplicate detection
      if (row[1]) {
        jobNumbers.push(row[1]);
      }
      
      // Collect customers
      if (row[3]) {
        customers.push(row[3]);
      }
      
      // Track priorities
      if (row[4]) {
        priorities[row[4]] = (priorities[row[4]] || 0) + 1;
      }
      
      // Analyze each field
      headers.forEach((header, index) => {
        if (!row[index] || row[index] === "") {
          missingCount[header] = (missingCount[header] || 0) + 1;
          
          // NEW: Track empty numeric fields specifically
          if ([5, 6, 7, 11, 12, 13].includes(index)) {
            if (!emptyNumericCount[header]) {
              emptyNumericCount[header] = [];
            }
            emptyNumericCount[header].push(rowNumber);
          }
        }
        
        // Numeric field analysis
        if ([5, 6, 7, 11, 12, 13].includes(index) && row[index] && row[index] !== "") {
          if (!numericData[header]) {
            numericData[header] = [];
          }
          const value = parseFloat(String(row[index]).replace(/,/g, ""));
          if (!isNaN(value)) {
            numericData[header].push(value);
          }
        }
      });
    });
    
    // NEW: Check for empty numeric fields and add warnings
    const criticalNumericFields = [5, 6, 7]; // WO Qty, Op Qty, Planned Qty
    criticalNumericFields.forEach(index => {
      const fieldName = headers[index];
      if (emptyNumericCount[fieldName] && emptyNumericCount[fieldName].length > 0) {
        const rowList = emptyNumericCount[fieldName].slice(0, 5).join(", ");
        const moreRows = emptyNumericCount[fieldName].length > 5 ? `, +${emptyNumericCount[fieldName].length - 5} more` : "";
        
        summary.warnings.push({
          type: "error",
          message: `${fieldName}: Empty in row(s) ${rowList}${moreRows}. Will be saved as NULL.`,
          icon: "⚠️"
        });
      }
    });
    
    summary.emptyNumericFields = emptyNumericCount;
    
    // Calculate numeric statistics
    Object.keys(numericData).forEach(field => {
      const values = numericData[field];
      if (values.length > 0) {
        const sum = values.reduce((a, b) => a + b, 0);
        const avg = sum / values.length;
        const min = Math.min(...values);
        const max = Math.max(...values);
        
        summary.numericStats[field] = {
          count: values.length,
          sum: sum.toFixed(2),
          average: avg.toFixed(2),
          min: min.toFixed(2),
          max: max.toFixed(2),
        };
      }
    });
    
    // Check for duplicates
    const duplicateJobs = jobNumbers.filter((num, idx) => jobNumbers.indexOf(num) !== idx);
    if (duplicateJobs.length > 0) {
      summary.duplicates = [...new Set(duplicateJobs)];
      summary.warnings.push({
        type: "warning",
        message: `Found ${summary.duplicates.length} duplicate Job Number(s): ${summary.duplicates.join(", ")}`,
        icon: "⚠️"
      });
    }
    
    // Unique customers
    const uniqueCustomers = [...new Set(customers)];
    summary.info.push({
      type: "info",
      message: `Data contains ${uniqueCustomers.length} unique customer(s)`,
      icon: "👥"
    });
    
    // Priority distribution
    if (Object.keys(priorities).length > 0) {
      const priorityText = Object.entries(priorities)
        .map(([priority, count]) => `${priority} (${count})`)
        .join(", ");
      summary.info.push({
        type: "info",
        message: `Priority distribution: ${priorityText}`,
        icon: "📊"
      });
    }
    
    // Missing data analysis
    Object.keys(missingCount).forEach(field => {
      const count = missingCount[field];
      const percentage = ((count / rows.length) * 100).toFixed(1);
      
      // Only report if significant missing data (skip numeric fields already reported)
      if (count > 0 && ![
        "No:", "Colour seq.", "Other details", "WO delay"
      ].includes(field)) {
        summary.missingData[field] = { count, percentage };
        
        // Don't duplicate warnings for numeric fields
        const fieldIndex = headers.indexOf(field);
        if (percentage > 50 && ![5, 6, 7, 11, 12, 13].includes(fieldIndex)) {
          summary.warnings.push({
            type: "warning",
            message: `${field}: ${percentage}% of rows are empty (${count}/${rows.length})`,
            icon: "⚠️"
          });
        }
      }
    });
    
    // Date field analysis
    const dateFields = [15, 16, 17, 18]; // Op start, Op end, WO due, WO end
    dateFields.forEach(index => {
      if (headers[index]) {
        const fieldName = headers[index];
        const datesPresent = rows.filter(row => row[index] && row[index] !== "").length;
        
        if (datesPresent === 0) {
          summary.warnings.push({
            type: "warning",
            message: `${fieldName}: No dates provided in any row`,
            icon: "📅"
          });
        }
      }
    });
    
    // Time unit analysis
    const timeUnits = rows.map(row => row[14]).filter(Boolean);
    const uniqueTimeUnits = [...new Set(timeUnits)];
    if (uniqueTimeUnits.length > 1) {
      summary.info.push({
        type: "info",
        message: `Multiple time units used: ${uniqueTimeUnits.join(", ")}`,
        icon: "⏱️"
      });
    } else if (uniqueTimeUnits.length === 1) {
      summary.info.push({
        type: "info",
        message: `Time unit: ${uniqueTimeUnits[0]}`,
        icon: "⏱️"
      });
    }
    
    // Data completeness score
    const totalFields = headers.length * rows.length;
    const filledFields = rows.reduce((count, row) => {
      return count + row.filter(cell => cell && cell !== "").length;
    }, 0);
    const completeness = ((filledFields / totalFields) * 100).toFixed(1);
    
    summary.info.unshift({
      type: "success",
      message: `Data completeness: ${completeness}% (${filledFields}/${totalFields} fields filled)`,
      icon: "✅"
    });
    
    summary.fieldAnalysis = {
      uniqueCustomers: uniqueCustomers.length,
      uniqueJobNumbers: [...new Set(jobNumbers)].length,
      priorityDistribution: priorities,
      timeUnits: uniqueTimeUnits,
    };
    
    return summary;
  };

  // ==================== FETCH FUNCTIONS ====================

  const fetchMachinesForGroup = useCallback(async (groupCode) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/MachineCodeWithNames`, {
        params: { groupCode },
      });
      setMachines(res.data || []);
    } catch (err) {
      console.error("Error fetching machines:", err);
      showAlert("Failed to load machines for the selected group.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMachineGroups = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/MachineGroupCodeWithNames`);
      setMachineGroups(res.data || []);
    } catch (err) {
      console.error("Error fetching machine groups:", err);
      showAlert("Failed to load machine groups.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch machine groups on mount
  useEffect(() => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/MachineGroupCodeWithNames`)
      .then(res => {
        setMachineGroups(res.data || []);
      })
      .catch(err => {
        console.error("Error fetching machine groups:", err);
        showAlert("Failed to load machine groups.", "error");
      })
      .finally(() => setLoading(false));
  }, []);

  // Fetch machines when a group is selected
  useEffect(() => {
    if (selectedGroup) {
      fetchMachinesForGroup(selectedGroup);
    } else {
      setMachines([]);  // clear machines if group deselected
    }
  }, [selectedGroup, fetchMachinesForGroup]);

  // Fetch existing planned jobs for the selected slot & machine
  const viewExistingPlan = useCallback(async () => {
    if (!selectedSlot || !selectedMachine) {
      showAlert("Please select both a time slot and a machine first.", "warning");
      return;
    }
    const { startDate, endDate } = getSlotTimeRange(selectedSlot - 1);
    const planningFrom = adjustDateTime(startDate);
    const planingTo = adjustDateTime(endDate);
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/ViewPlannedJobs`, {
        params: { machineCode: selectedMachine, planningFrom, planingTo }
      });
      if (response.status === 200 && response.data) {
        const jobs = response.data;
        if (jobs.length > 0) {
          setExistingPlanData(jobs);
          setExpandedPlanTable(false);
          showAlert("Plan data loaded successfully.", "success");
        } else {
          setExistingPlanData([]);
          setUploadNewplanButtonVisible(true);
          showAlert("No existing plan found for this period.", "info");
        }
      } else {
        console.error("Unexpected response for ViewPlannedJobs:", response);
        setExistingPlanData([]);
        showAlert("Failed to load plan data.", "error");
      }
    } catch (error) {
      console.error("Error fetching plan data:", error);
      setExistingPlanData([]);
      showAlert("Error fetching plan data.", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedSlot, selectedMachine]);

  // Fetch lineup jobs for the selected machine
  const viewExistingLineup = useCallback(async () => {
    if (!selectedMachine) {
      showAlert("Please select a machine first.", "warning");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/fetchLineupJobs`, {
        machineCode: selectedMachine,
      });
      if (response.status === 200 && response.data) {
        const lineupJobs = response.data;
        if (lineupJobs.length > 0) {
          setExistingLineupData(lineupJobs);
          setExpandedLineupTable(false);
          setUploadNewLineupButtonVisible(false);
          showAlert("Lineup data loaded successfully.", "success");
        } else {
          setExistingLineupData([]);
          setUploadNewLineupButtonVisible(true);
          showAlert("No lineup data found for this machine.", "info");
        }
      } else {
        console.error("Unexpected response for ViewLineupJobs:", response);
        setExistingLineupData([]);
        setUploadNewLineupButtonVisible(true);
        showAlert("Failed to load lineup data.", "error");
      }
    } catch (error) {
      console.error("Error fetching lineup data:", error);
      setExistingLineupData([]);
      setUploadNewLineupButtonVisible(true);
      showAlert("Error fetching lineup data.", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedMachine]);

  // Automatically fetch plan/lineup data when slot or machine selection changes (and both are selected)
  useEffect(() => {
    if (!selectedSlot || !selectedMachine) {
      return;
    }
    // Both slot and machine are selected
    if (viewMode === "plan") {
      viewExistingPlan();
    } else if (viewMode === "lineup") {
      viewExistingLineup();
    }
  }, [selectedSlot, selectedMachine, viewMode, viewExistingPlan, viewExistingLineup]);

  // Update step indicator index based on selections
  const activeStep = selectedSlot
    ? (selectedMachine ? 2 : 1)
    : 0;

  // ==================== SELECTION HANDLERS ====================

  const handleSlotChange = (event) => {
    const slot = event.target.value;
    setSelectedSlot(slot);
    setSelectedGroup("");
    setSelectedMachine("");
    setExistingPlanData([]);
    setExistingLineupData([]);
    setExpandedPlanTable(false);
    setExpandedLineupTable(false);
    setUploadNewplanButtonVisible(false);
    setPastingBlockVisible(false);
    setData(null);
    setExcelData("");
  };

  const handleGroupChange = (event) => {
    const group = event.target.value;
    setSelectedGroup(group);
    setSelectedMachine("");
    setExistingPlanData([]);
    setExistingLineupData([]);
    setExpandedPlanTable(false);
    setExpandedLineupTable(false);
    setUploadNewplanButtonVisible(false);
    setPastingBlockVisible(false);
    setData(null);
    setExcelData("");
  };

  const handleMachineChange = (event) => {
    const machine = event.target.value;
    setSelectedMachine(machine);
    setExistingPlanData([]);
    setExistingLineupData([]);
    setExpandedPlanTable(false);
    setExpandedLineupTable(false);
    setUploadNewplanButtonVisible(false);
    setPastingBlockVisible(false);
    setData(null);
    setExcelData("");
  };

  // Toggle between Plan and Lineup view
  const handleViewModeChange = (event, newMode) => {
    if (!newMode) return;
    if (newMode === viewMode) return;  // no change
    setViewMode(newMode);
    if (newMode === "plan") {
      viewExistingPlan();
    } else if (newMode === "lineup") {
      viewExistingLineup();
    }
  };

  // ==================== PLAN DATA VALIDATION & PROCESSING ====================

  const handleExcelDataChange = (e) => {
    setExcelData(e.target.value);
  };

  const handlePasteExcelData = () => {
    if (!excelData.trim()) return;
    setDataProcessing(true);
    
    const validationErrors = [];
    
    try {
      const wb = XLSX.read(excelData, { type: "binary", cellDates: false });
      
      if (wb.SheetNames.length === 0) {
        throw new Error("No sheets found in the pasted data.");
      }
      
      const ws = wb.Sheets[wb.SheetNames[0]];
      const parsedExcelData = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
      
      if (parsedExcelData.length === 0) {
        throw new Error("No data rows found in the pasted data.");
      }
      
      // Trim whitespace
      const trimmedData = parsedExcelData.map(row =>
        row.map(cell => (typeof cell === "string" ? cell.trim() : cell))
      );
      
      const headerRow = trimmedData[0];
      
      // 1. HEADER VALIDATION - More flexible
      const hasRequiredColumns = [
        "Job No:",
        "WO description",
        "Customer",
      ].every(col => headerRow.includes(col));
      
      if (!hasRequiredColumns) {
        throw new Error("Missing required columns. Please ensure Job No:, WO description, and Customer columns are present.");
      }
      
      // 2. VALIDATE DATA ROWS
      const dataRows = trimmedData.slice(1);
      const validatedRows = [];
      let emptyRowCount = 0;
      
      dataRows.forEach((row, rowIndex) => {
        const actualRowNumber = rowIndex + 2;
        const rowErrors = [];
        
        // 2a. Check if row is completely empty
        const isEmptyRow = row.every(cell => !cell || cell === "");
        if (isEmptyRow) {
          emptyRowCount++;
          return; // Skip this row silently
        }
        
        // 2b. REQUIRED FIELD VALIDATION - Only essential fields
        const requiredFields = [
          { index: 1, name: "Job No:" },
          { index: 2, name: "WO description" },
          { index: 3, name: "Customer" },
        ];
        
        requiredFields.forEach(field => {
          if (!row[field.index] || row[field.index] === "") {
            rowErrors.push(`${field.name} is required`);
          }
        });
        
        // 2c. NUMERIC FIELD VALIDATION - Only validate if field has value
        const numericFields = [
          { index: 5, name: "WO quantity", min: 0 },
          { index: 6, name: "Op. quantity", min: 0 },
          { index: 7, name: "Planned quantity", min: 0 },
          { index: 11, name: "Setup Time", min: 0 },
          { index: 12, name: "Operational Time", min: 0 },
          { index: 13, name: "Setup+ Operational Time", min: 0 },
        ];
        
        numericFields.forEach(field => {
          if (row[field.index] && row[field.index] !== "") {
            const cleanValue = String(row[field.index]).replace(/,/g, "");
            const numValue = parseFloat(cleanValue);
            
            if (isNaN(numValue)) {
              rowErrors.push(`${field.name} must be a valid number (got: "${row[field.index]}")`);
            } else if (numValue < field.min) {
              rowErrors.push(`${field.name} must be >= ${field.min} (got: ${numValue})`);
            } else {
              // Update row with cleaned numeric value
              row[field.index] = cleanValue;
            }
          }
        });
        
        // Add row errors to main validation errors
        if (rowErrors.length > 0) {
          validationErrors.push(`Row ${actualRowNumber} (Job: ${row[1] || 'N/A'}): ${rowErrors.join(", ")}`);
        } else {
          validatedRows.push(row);
        }
      });
      
      // 3. SHOW VALIDATION RESULTS
      if (validationErrors.length > 0) {
        setValidationErrors(validationErrors);
        setShowErrorDialog(true);
        setData(null);
        return;
      }
      
      // 4. SUCCESS - No validation errors
      if (validatedRows.length === 0) {
        throw new Error("No valid data rows found after validation.");
      }
      
      const processedData = [headerRow, ...validatedRows];
      setData(processedData);
      
      // 5. GENERATE DATA SUMMARY
      const summary = analyzeData(processedData, "plan");
      setDataSummary(summary);
      
      showAlert(
        `✓ Successfully validated ${validatedRows.length} row(s). ${emptyRowCount > 0 ? `(${emptyRowCount} empty rows removed)` : ""}`,
        "success"
      );
      setPastingBlockVisible(false);
      
      // 6. SHOW SUMMARY DIALOG
      setSummaryType("plan");
      setShowSummaryDialog(true);
      
    } catch (error) {
      console.error("Error processing Excel data:", error);
      showAlert(error.message || "Failed to process Excel data. Please check the format.", "error");
      setData(null);
    } finally {
      setDataProcessing(false);
    }
  };

  // Clear the pasted Excel data input
  const handleClearPastedData = () => {
    setExcelData("");
  };

  // Cancel preview (discard the processed data preview)
  const handleClearPreview = () => {
    setData(null);
    setDataSummary(null);
    // If no existing plan, allow starting upload again
    if (existingPlanData.length === 0) {
      setUploadNewplanButtonVisible(true);
    }
    // Show paste input again for editing if Excel data exists
    if (excelData.trim()) {
      setPastingBlockVisible(true);
    }
  };

  // ==================== LINEUP DATA VALIDATION & PROCESSING ====================

  const handleLineupExcelDataChange = (e) => {
    setLineupExcelData(e.target.value);
  };

  // UPDATED STRICT VALIDATION FOR LINEUP DATA
  const handlePasteLineupData = () => {
    if (!lineupExcelData.trim()) return;
    setLineupDataProcessing(true);
    
    const validationErrors = [];
    
    try {
      const wb = XLSX.read(lineupExcelData, { type: "binary", cellDates: false });
      
      if (wb.SheetNames.length === 0) {
        throw new Error("No sheets found in the pasted data.");
      }
      
      const ws = wb.Sheets[wb.SheetNames[0]];
      const parsedExcelData = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
      
      if (parsedExcelData.length === 0) {
        throw new Error("No data rows found in the pasted data.");
      }
      
      const trimmedData = parsedExcelData.map(row =>
        row.map(cell => (typeof cell === "string" ? cell.trim() : cell))
      );
      
      const headerRow = trimmedData[0];

      // 1. STRICT HEADER VALIDATION (MATCHING PLAN)
      const hasRequiredColumns = [
        "Job No:",
        "WO description",
        "Customer",
      ].every(col => headerRow.includes(col));
      
      if (!hasRequiredColumns) {
        throw new Error("Missing required columns. Please ensure Job No:, WO description, and Customer columns are present.");
      }

      const dataRows = trimmedData.slice(1);
      const validatedRows = [];
      let emptyRowCount = 0;
      
      // LINEUP-SPECIFIC VALIDATION (MATCHING PLAN LOGIC EXACTLY)
      dataRows.forEach((row, rowIndex) => {
        const actualRowNumber = rowIndex + 2;
        const rowErrors = [];
        
        // 2a. Empty row check
        const isEmptyRow = row.every(cell => !cell || cell === "");
        if (isEmptyRow) {
          emptyRowCount++;
          return;
        }
        
        // 2b. Required fields (MATCHING PLAN STRUCTURE)
        const requiredFields = [
          { index: 1, name: "Job No:" },
          { index: 2, name: "WO description" },
          { index: 3, name: "Customer" },
        ];
        
        requiredFields.forEach(field => {
          if (!row[field.index] || row[field.index] === "") {
            rowErrors.push(`${field.name} is required`);
          }
        });
        
        // 2c. Numeric validations (MATCHING PLAN STRUCTURE & NAMES)
        const numericFields = [
          { index: 5, name: "WO quantity", min: 0 },
          { index: 6, name: "Op. quantity", min: 0 },
          { index: 7, name: "Planned quantity", min: 0 },
          { index: 11, name: "Setup Time", min: 0 },
          { index: 12, name: "Operational Time", min: 0 },
          { index: 13, name: "Setup+ Operational Time", min: 0 },
        ];
        
        numericFields.forEach(field => {
          if (row[field.index] && row[field.index] !== "") {
            const cleanValue = String(row[field.index]).replace(/,/g, "");
            const numValue = parseFloat(cleanValue);
            
            if (isNaN(numValue)) {
              rowErrors.push(`${field.name} must be a valid number (got: "${row[field.index]}")`);
            } else if (numValue < field.min) {
              rowErrors.push(`${field.name} must be >= ${field.min} (got: ${numValue})`);
            } else {
              // Update row with cleaned numeric value
              row[field.index] = cleanValue;
            }
          }
        });
        
        if (rowErrors.length > 0) {
          validationErrors.push(`Row ${actualRowNumber} (Job: ${row[1] || 'N/A'}): ${rowErrors.join(", ")}`);
        } else {
          validatedRows.push(row);
        }
      });
      
      if (validationErrors.length > 0) {
        setValidationErrors(validationErrors);
        setShowErrorDialog(true);
        setLineupData(null);
        return;
      }
      
      if (validatedRows.length === 0) {
        throw new Error("No valid data rows found after validation.");
      }
      
      const processedData = [headerRow, ...validatedRows];
      setLineupData(processedData);
      
      // GENERATE LINEUP SUMMARY
      const summary = analyzeData(processedData, "lineup");
      setLineupDataSummary(summary);
      
      showAlert(
        `✓ Successfully validated ${validatedRows.length} lineup row(s). ${emptyRowCount > 0 ? `(${emptyRowCount} empty rows removed)` : ""}`,
        "success"
      );
      setLineupPasteBlockVisible(false);
      
      // SHOW SUMMARY DIALOG
      setSummaryType("lineup");
      setShowSummaryDialog(true);
      
    } catch (error) {
      console.error("Error processing lineup data:", error);
      showAlert(error.message || "Failed to process lineup data.", "error");
      setLineupData(null);
    } finally {
      setLineupDataProcessing(false);
    }
  };

  const handleClearLineupPastedData = () => {
    setLineupExcelData("");
  };

  const handleClearLineupPreview = () => {
    setLineupData(null);
    setLineupDataSummary(null);
    if (existingLineupData.length === 0) {
      setUploadNewLineupButtonVisible(true);
    }
    if (lineupExcelData.trim()) {
      setLineupPasteBlockVisible(true);
    }
  };

  // ==================== UPLOAD HANDLERS ====================

  const handleUploadPlan = () => {
    if (!selectedSlot || !selectedMachine || !data || data.length <= 1) {
      showAlert("No plan data to upload.", "warning");
      return;
    }
    
    // FINAL PRE-UPLOAD VALIDATION
    const finalErrors = [];
    const jobs = data.slice(1);
    
    jobs.forEach((row, idx) => {
      // Check critical fields one more time
      if (!row[1]) finalErrors.push(`Row ${idx + 2}: Missing Job Number`);
      if (!row[3]) finalErrors.push(`Row ${idx + 2}: Missing Customer`);
    });
    
    if (finalErrors.length > 0) {
      setValidationErrors(finalErrors);
      setShowErrorDialog(true);
      return;
    }
    
    // Prepare payload
    const { startDate, endDate } = getSlotTimeRange(selectedSlot - 1);
    const payload = {
      machineCode: selectedMachine,
      planningFrom: adjustDateTime(startDate),
      planingTo: adjustDateTime(endDate),
      jobs: data.slice(1).map(row => {
        try {
          return {
            jobSequenceNumber: row[0] || null,
            jobNumber: row[1] || null,
            WOdescription: row[2] || null,
            customer: row[3] || null,
            WOpriority: row[4] || null,
            // Convert empty strings to null for numeric fields
            WOQty: row[5] && row[5] !== "" ? row[5] : null,
            OPQty: row[6] && row[6] !== "" ? row[6] : null,
            plannedQty: row[7] && row[7] !== "" ? row[7] : null,
            OpDesctiption: row[8] || null,
            ColorSequence: row[9] || null,
            OtherDetails: row[10] || null,
            SetupTime: row[11] && row[11] !== "" ? row[11] : null,
            OperationalTime: row[12] && row[12] !== "" ? row[12] : null,
            SetupAndOpeTime: row[13] && row[13] !== "" ? row[13] : null,
            TimeUnit: row[14] || null,
            opstartDate: row[15] || null,
            opendDate: row[16] || null,
            WOduedate: row[17] || null,
            WOenddate: row[18] || null,
            WOdelay: row[19] || null
          };
        } catch {
          return null;
        }
      }).filter(item => item !== null)
    };
    
    setLoading(true);
    axios.post(`${API_BASE_URL}/uploadPlan`, payload)
      .then(res => {
        showAlert("Plan uploaded successfully!", "success");
        // Clear temporary states
        setData(null);
        setDataSummary(null);
        setExcelData("");
        setPastingBlockVisible(false);
        setUploadNewplanButtonVisible(false);
        // Refresh the plan view to display the newly uploaded data
        viewExistingPlan();
      })
      .catch(err => {
        console.error("Error uploading plan:", err);
        const errMsg = err.response?.data?.error || "Upload failed. Please try again.";
        showAlert(errMsg, "error");
      })
      .finally(() => setLoading(false));
  };

  // UPDATED STRICT UPLOAD HANDLER FOR LINEUP
  const handleUploadLineup = async () => {
    if (!selectedMachine || !lineupData || lineupData.length <= 1) {
      showAlert("No lineup data to upload.", "warning");
      return;
    }

    // --- FINAL PRE-UPLOAD VALIDATION (MATCHING PLAN) ---
    const finalErrors = [];
    const rowsToCheck = lineupData.slice(1);
    
    rowsToCheck.forEach((row, idx) => {
      // Check critical fields one more time
      if (!row[1]) finalErrors.push(`Row ${idx + 2}: Missing Job Number`);
      if (!row[3]) finalErrors.push(`Row ${idx + 2}: Missing Customer`);
    });
    
    if (finalErrors.length > 0) {
      setValidationErrors(finalErrors);
      setShowErrorDialog(true);
      return;
    }
    // ----------------------------------------

    const [, ...rows] = lineupData;

    const fieldMapping = [
      "jobSequenceNumber", "jobNumber", "WOdescription", "customer", "WOpriority",
      "WOQty", "OPQty", "plannedQty", "OpDesctiption", "ColorSequence",
      "OtherDetails", "SetupTime", "OperationalTime", "SetupAndOpeTime", "TimeUnit",
      "opstartDate", "opendDate", "WOduedate", "WOenddate", "WOdelay"
    ];

    const jobs = rows.map(row => {
      const job = {};
      fieldMapping.forEach((key, idx) => {
        let value = row[idx];
        
        // Remove commas from numeric strings
        if (["WOQty", "OPQty", "plannedQty", "SetupTime", "OperationalTime", "SetupAndOpeTime"].includes(key)) {
          if (value && value !== "") {
            value = typeof value === "string" ? value.replace(/,/g, "") : value;
          } else {
            value = null; // Convert empty to null
          }
        }
        
        // Convert empty strings to null for all fields
        job[key] = (value === "" || value === undefined) ? null : value;
      });
      return job;
    });

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/uploadLineupJobs`, {
        machineCode: selectedMachine,
        jobs: jobs
      });

      showAlert("Lineup uploaded successfully!", "success");
      setLineupData(null);
      setLineupDataSummary(null);
      setLineupExcelData("");
      setLineupPasteBlockVisible(false);
      setUploadNewLineupButtonVisible(false);
      viewExistingLineup();
    } catch (error) {
      console.error("Error uploading lineup:", error);
      const errMsg = error.response?.data?.error || "Upload failed. Please try again.";
      showAlert(errMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  // ==================== DELETE HANDLERS ====================

  const handleDeletePlan = () => {
    const { startDate, endDate } = getSlotTimeRange(selectedSlot - 1);
    const planningFrom = adjustDateTime(startDate);
    const planingTo = adjustDateTime(endDate);
    setPlanConfirmOpen(false);
    setLoading(true);
    axios.delete(`${API_BASE_URL}/DeletePlannedJobs`, {
      params: { machineCode: selectedMachine, planningFrom, planingTo }
    })
      .then(() => {
        setExistingPlanData([]);
        setExpandedPlanTable(false);
        // Allow uploading a new plan now
        setPastingBlockVisible(true);
        setUploadNewplanButtonVisible(false);
        setData(null);
        setExcelData("");
        showAlert("Plan deleted successfully.", "success");
      })
      .catch(err => {
        console.error("Error deleting plan:", err);
        showAlert("Failed to delete the plan.", "error");
      })
      .finally(() => setLoading(false));
  };

  const handleDeleteLineup = () => {
    setLineupConfirmOpen(false);
    setLoading(true);
    axios.delete(`${API_BASE_URL}/DeleteLineupJobs`, { params: { machineCode: selectedMachine } })
      .then(() => {
        setExistingLineupData([]);
        setExpandedLineupTable(false);
        setLineupPasteBlockVisible(true);
        setUploadNewLineupButtonVisible(false);
        setLineupData(null);
        setLineupExcelData("");
        showAlert("Lineup cleared successfully.", "success");
      })
      .catch(err => {
        console.error("Error deleting lineup:", err);
        showAlert("Failed to clear the lineup.", "error");
      })
      .finally(() => setLoading(false));
  };

  // ==================== RENDER ====================

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Card elevation={3} sx={{ mb: 4, background: "linear-gradient(to right, #2563EB, #7e22ce)", color: "#fff" }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {/* Title and Breadcrumbs */}
            <Stack direction="row" alignItems="center" spacing={3}>
              <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)" }}>
                <UploadIcon color="#ffffff" size={28} />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight="bold" sx={{ color: "#fff" }}>
                  Planning Upload Center
                </Typography>
                <Typography variant="subtitle1" sx={{ color: "rgba(255,255,255,0.8)" }}>
                  Streamlined production planning and data management
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 0.5, color: "rgba(255,255,255,0.8)" }}>
                  <Home style={{ width: 16, height: 16, marginRight: 4, color: "inherit" }} />
                  <Typography variant="caption" sx={{ color: "inherit" }}>Dashboard</Typography>
                  <ChevronRight style={{ width: 16, height: 16, margin: "0 4px", color: "inherit" }} />
                  <Typography variant="caption" sx={{ color: "inherit" }}>Planning Upload</Typography>
                </Box>
              </Box>
            </Stack>
            {/* Badges */}
            <Stack direction="row" spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", backgroundColor: "rgba(255,255,255,0.2)", px: 2, py: 0.5, borderRadius: "16px" }}>
                <Shield style={{ width: 18, height: 18, marginRight: 8, color: "#fff" }} />
                <Typography variant="body2" sx={{ color: "#fff" }}>Secure Upload</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", backgroundColor: "rgba(255,255,255,0.2)", px: 2, py: 0.5, borderRadius: "16px" }}>
                <TrendingUp style={{ width: 18, height: 18, marginRight: 8, color: "#fff" }} />
                <Typography variant="body2" sx={{ color: "#fff" }}>Real-time Processing</Typography>
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={1} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700}>Find a job’s planning history</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Start typing a job number. Select a suggestion or open the exact number entered.
          </Typography>
          <Box component="form" onSubmit={(event) => {
            event.preventDefault();
            if (historySearch.trim()) setHistoryJobNumber(historySearch.trim());
          }} sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 1 }}>
            <Autocomplete freeSolo openOnFocus options={jobSuggestions} loading={suggestionsLoading}
              filterOptions={(options) => options} inputValue={historySearch}
              onInputChange={(event, value, reason) => {
                if (reason !== "reset") setHistorySearch(value);
              }}
              onChange={(event, option) => {
                if (!option) return;
                const number = typeof option === "string" ? option : option.jobNumber;
                setHistorySearch(number);
                setHistoryJobNumber(number);
              }}
              getOptionLabel={(option) => typeof option === "string" ? option : option.jobNumber}
              noOptionsText={historySearch.trim().length < 3 ? "Type at least 3 characters" : "No matching current jobs"}
              sx={{ flex: "1 1 320px" }}
              renderInput={(params) => <TextField {...params} size="small" label="Job number"
                inputProps={{ ...params.inputProps, maxLength: 64 }}
                helperText={suggestionsError || "Letters, numbers and hyphens"}
                InputProps={{ ...params.InputProps, endAdornment: <>
                  {suggestionsLoading && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </> }} />}
              renderOption={(props, option) => (
                <li {...props} key={option.jobNumber}>
                  <Box sx={{ width: "100%", py: 0.25 }}>
                    <Stack direction="row" justifyContent="space-between" gap={1}>
                      <Typography fontWeight={700}>{option.jobNumber}</Typography>
                      {option.matchType === "similar" && <Chip size="small" label="Similar" />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {option.customer || "Customer unavailable"} · {option.machines || "—"} · {option.entryCount} plans
                    </Typography>
                  </Box>
                </li>
              )} />
            <Button type="submit" variant="contained" disabled={!historySearch.trim()} startIcon={<Eye size={18} />}
              sx={{ minHeight: 40 }}>
              View exact job
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Stepper Progress Indicator */}
      <Card elevation={1} sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            {steps.map((step, index) => {
              const IconComp = step.icon;
              const isCompleted = index < activeStep;
              const isActive = index <= activeStep;
              return (
                <Box key={step.label} sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                  {/* Step circle and label */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box sx={{
                      width: 48, height: 48, borderRadius: "50%",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: isActive ? "linear-gradient(to right, #3B82F6, #A855F7)" : "#e0e0e0",
                      color: isActive ? "#fff" : "#9e9e9e",
                      boxShadow: isActive ? 3 : "none",
                      transition: "background-color 0.3s"
                    }}>
                      {isCompleted ? (
                        <CheckCircle size={24} style={{ color: isActive ? "#fff" : "#9e9e9e" }} />
                      ) : (
                        <IconComp color={isActive ? "#fff" : "#9e9e9e"} size={24} style={{ verticalAlign: "middle" }} />
                      )}
                    </Box>
                    <Typography variant="body2" fontWeight={isActive ? "bold" : "normal"} sx={{ mt: 1, color: isActive ? "text.primary" : "text.disabled", textAlign: "center" }}>
                      {step.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "center" }}>
                      {step.description}
                    </Typography>
                  </Box>
                  {/* Connector line */}
                  {index < steps.length - 1 && (
                    <Box sx={{
                      flex: 1, height: 4, mx: 2, borderRadius: "2px",
                      background: index < activeStep ? "linear-gradient(to right, #3B82F6, #A855F7)" : "#e0e0e0"
                    }} />
                  )}
                </Box>
              );
            })}
          </Box>
          {loading && (
            <LinearProgress sx={{ mt: 2 }} />
          )}
        </CardContent>
      </Card>

      {/* Selection Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Time Slot Selection */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={3}
            sx={{
              border: selectedSlot ? "2px solid #1976d2" : "1px solid #e0e0e0",
              transition: "all 0.3s",
              borderRadius: 3,
              '&:hover': {
                boxShadow: 6,
                transform: "translateY(-4px)",
              },
              position: "relative",
            }}
          >
            {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3 }} />}
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: "primary.main" }}>
                  <Calendar color="#fff" size={24} />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">Time Slot Selection</Typography>
                {selectedSlot && <Chip label="Selected" color="primary" size="small" icon={<CheckCircle size={16} />} />}
              </Stack>
              <FormControl fullWidth size="medium">
                <InputLabel>Select Time Slot</InputLabel>
                <Select value={selectedSlot} label="Select Time Slot" onChange={handleSlotChange}>
                  {[1, 2, 3, 4, 5].map(slot => {
                    const range = getSlotTimeRange(slot - 1);
                    return (
                      <MenuItem key={slot} value={slot}>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">Time Slot {slot}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {range.startDate.format("MMM DD, YYYY HH:mm")} - {range.endDate.format("MMM DD, YYYY HH:mm")}
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* Machine Selection */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={3}
            sx={{
              border: selectedSlot ? "2px solid #1976d2" : "1px solid #e0e0e0",
              transition: "all 0.3s",
              borderRadius: 3,
              '&:hover': {
                boxShadow: 6,
                transform: "translateY(-4px)",
              },
              position: "relative",
            }}
          >
            {loading && (
              <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3 }} />
            )}
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: "secondary.main" }}>
                  <Factory color="#fff" size={24} />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">Machine Selection</Typography>
                {selectedMachine && <Chip label="Selected" color="secondary" size="small" icon={<CheckCircle size={16} />} />}
              </Stack>
              <Fade in={Boolean(selectedSlot)}>
                <Box>
                  <FormControl fullWidth size="medium" disabled={!selectedSlot} sx={{ mb: 2 }}>
                    <InputLabel>Machine Group</InputLabel>
                    <Select value={selectedGroup} label="Machine Group" onChange={handleGroupChange} onOpen={() => {
                      if (!machineGroups.length && !loading) {
                        fetchMachineGroups();
                      }
                    }}>
                      {machineGroups.map(group => (
                        <MenuItem key={group.MachineGroupCode} value={group.MachineGroupCode}>
                          {group.GroupDescription}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="medium" disabled={!selectedGroup}>
                    <InputLabel>Machine</InputLabel>
                    <Select value={selectedMachine} label="Machine" onChange={handleMachineChange} onOpen={() => {
                      if (selectedGroup && !machines.length && !loading) {
                        fetchMachinesForGroup(selectedGroup);
                      }
                    }}>
                      {machines.map(machine => (
                        <MenuItem key={machine.MachineCode} value={machine.MachineCode}>
                          {machine.MachineDescription} (Code: {machine.MachineCode})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Fade>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* View Mode Toggle (Plan vs Lineup) */}
      {selectedSlot && selectedMachine && (
        <Card elevation={1} sx={{ mb: 4 }}>
          <CardContent>
            <Stack direction="row" justifyContent="center">
              <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} color="primary">
                <ToggleButton value="plan">
                  <FileText style={{ width: 18, height: 18, marginRight: 4 }} /> Production Plan
                </ToggleButton>
                <ToggleButton value="lineup">
                  <Database style={{ width: 18, height: 18, marginRight: 4 }} /> Machine Lineup
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Existing Plan Data View */}
      {viewMode === "plan" && selectedSlot && selectedMachine && (
        <>
          {existingPlanData.length > 0 && (
            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: "success.main" }}>
                      <Info color="#fff" size={20} />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">Existing Plan Data</Typography>
                    <Chip label={`${existingPlanData.length} Jobs`} color="success" variant="outlined" />
                  </Stack>
                  <IconButton onClick={() => setExpandedPlanTable(!expandedPlanTable)} color="primary">
                    {expandedPlanTable ? <ChevronUp /> : <ChevronDown />}
                  </IconButton>
                </Stack>
                <Collapse in={expandedPlanTable} timeout="auto" unmountOnExit>
                  <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 2 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {[
                            "Job Sequence", "Job Number", "WO Description", "Customer", "WO Priority",
                            "WO Qty", "OP Qty", "Planned Qty", "OP Description", "Setup Time",
                            "Operational Time", "Setup + Ope Time", "Time Unit", "OP Start Date",
                            "OP End Date", "WO Due Date", "WO End Date", "WO Delay"
                          ].map(header => (
                            <TableCell key={header} sx={{ fontWeight: "bold", backgroundColor: "primary.main", color: "#fff", whiteSpace: "nowrap" }}>
                              {header}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {existingPlanData.map((job) => (
                          <TableRow key={job.jobSequenceNumber} hover>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.jobSequenceNumber}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}><Button size="small" variant="text" onClick={() => setHistoryJobNumber(String(job.jobNumber))} aria-label={`View planning history for job ${job.jobNumber}`}>{job.jobNumber}</Button></TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.WOdescription}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.customer}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.WOpriority}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.WOQty}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.OPQty}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.plannedQty}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.OpDesctiption}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.SetupTime}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.OperationalTime}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.SetupAndOpeTime}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.TimeUnit}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.opstartDate}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.opendDate}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.WOduedate}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.WOenddate}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{job.WOdelay}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
                <Stack direction="row" justifyContent="center">
                  <Button variant="contained" color="error" startIcon={<Trash2 style={{ width: 18, height: 18 }} />} onClick={() => setPlanConfirmOpen(true)}>
                    Remove & Upload New Plan
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Prompt to upload new plan if none exists */}
          {uploadNewplanButtonVisible && !pastingBlockVisible && (
            <Card elevation={1} sx={{ mb: 3 }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Box sx={{ bgcolor: "green.100", width: 64, height: 64, borderRadius: "50%", mx: "auto", mb: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Plus color="#2e7d32" size={32} />
                </Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Ready to Upload New Plan</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  No existing plan found. You can upload a new production plan for this slot.
                </Typography>
                <Button variant="contained" onClick={() => setPastingBlockVisible(true)} startIcon={<UploadIcon size={20} />} sx={{ px: 4, py: 1 }}>
                  Start Upload Process
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* LINEUP VIEW SECTION */}
      {viewMode === "lineup" && selectedMachine && (
        <>
          {/* Existing Lineup Data */}
          {existingLineupData.length > 0 && (
            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: "secondary.main" }}>
                      <LucideInfo color="#fff" size={20} />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">Machine Lineup</Typography>
                    <Chip label={`${existingLineupData.length} Jobs`} color="secondary" variant="outlined" />
                  </Stack>
                  <IconButton onClick={() => setExpandedLineupTable(!expandedLineupTable)} color="primary">
                    {expandedLineupTable ? <ChevronUp /> : <ChevronDown />}
                  </IconButton>
                </Stack>
                <Collapse in={expandedLineupTable} timeout="auto" unmountOnExit>
                  <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 2 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Job No</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Customer</TableCell>
                          <TableCell>Priority</TableCell>
                          <TableCell>Quantity</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {existingLineupData.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{row.jobNumber}</TableCell>
                            <TableCell>{row.WOdescription}</TableCell>
                            <TableCell>{row.customer}</TableCell>
                            <TableCell>{row.WOpriority}</TableCell>
                            <TableCell>{row.WOQty}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
                <Stack direction="row" justifyContent="center">
                  <Button variant="contained" color="error" startIcon={<Trash2 size={18} />} onClick={() => setLineupConfirmOpen(true)}>
                    Remove & Upload New Lineup
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Prompt to upload new lineup if none exists */}
          {uploadNewLineupButtonVisible && !lineupPasteBlockVisible && !lineupData && (
            <Card elevation={1} sx={{ mb: 3 }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Box sx={{
                  bgcolor: "green.100",
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  mx: "auto",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <Plus color="#2e7d32" size={32} />
                </Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                  Ready to Upload New Lineup
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  No existing lineup found. You can upload a new machine lineup for this machine.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon size={20} />}
                  sx={{ px: 4, py: 1 }}
                  onClick={() => setLineupPasteBlockVisible(true)}
                >
                  Start Upload Process
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Paste Block for Lineup */}
          <Collapse in={lineupPasteBlockVisible} timeout="auto" unmountOnExit>
            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: "info.main" }}>
                    <CopyIcon color="#fff" size={20} />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold">Excel Data Input</Typography>
                </Stack>
                <Alert severity="info" variant="outlined" icon={<LucideInfo size={20} />} sx={{ mb: 2 }}>
                  <strong>Required:</strong> Job No:, WO description, Customer. <strong>Optional:</strong> All other fields including dates and time units.
                </Alert>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  placeholder="Paste your Excel table data here..."
                  value={lineupExcelData}
                  onChange={handleLineupExcelDataChange}
                  sx={{ mb: 2, fontFamily: "monospace" }}
                  helperText={
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        ✓ Paste data directly from Excel (Ctrl+C, then Ctrl+V here)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ✓ Required: Job No:, WO description, Customer
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ✓ Numeric fields: WO quantity, Op. quantity, Planned quantity
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ✓ All other fields (dates, time units) accepted as-is
                      </Typography>
                      {lineupExcelData && (
                        <Typography variant="caption" color="success.main" sx={{ fontWeight: "bold" }}>
                          ✓ {lineupExcelData.split('\n').length} lines pasted
                        </Typography>
                      )}
                    </Stack>
                  }
                />
                <Stack direction="row" justifyContent="center" spacing={2}>
                  <Button
                    variant="contained"
                    onClick={handlePasteLineupData}
                    disabled={!lineupExcelData.trim() || lineupDataProcessing}
                    startIcon={lineupDataProcessing ?
                      <CircularProgress size={16} color="inherit" /> :
                      <CopyIcon size={18} />
                    }
                  >
                    {lineupDataProcessing ? "Processing..." : "Process Lineup Data"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleClearLineupPastedData}
                    startIcon={<XIcon size={18} />}
                    disabled={!lineupExcelData.trim() || lineupDataProcessing}
                  >
                    Clear Data
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Collapse>

          {/* Lineup Data Preview Table */}
          {lineupData && lineupData.length > 0 && (
            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: "warning.main" }}>
                    <Eye color="#fff" size={20} />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold">Lineup Data Preview</Typography>
                  <Chip label={`${lineupData.length - 1} Rows`} color="warning" variant="outlined" />
                </Stack>
                <TableContainer component={Paper} sx={{ maxHeight: 500, mb: 3, border: "1px solid #e0e0e0" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {lineupData[0].map((headerCell, idx) => (
                          <TableCell key={idx} sx={{ fontWeight: "bold", backgroundColor: "warning.main", color: "#fff", whiteSpace: "nowrap" }}>
                            {headerCell}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineupData.slice(1).map((row, rowIndex) => (
                        <TableRow key={rowIndex} hover>
                          {row.map((cell, cellIndex) => (
                            <TableCell key={cellIndex} sx={{ whiteSpace: "nowrap", borderRight: cellIndex === row.length - 1 ? "none" : "1px solid #e0e0e0" }}>
                              {cell !== null && cell !== "" ? cell : "-"}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Stack direction="row" justifyContent="center" spacing={2}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleUploadLineup}
                    startIcon={<UploadIcon size={20} />}
                    disabled={loading || !lineupData.length}
                    sx={{ px: 4 }}
                  >
                    Confirm & Upload Lineup
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleClearLineupPreview}
                    startIcon={<XIcon size={20} />}
                    sx={{ px: 4 }}
                  >
                    Cancel Preview
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Excel Data Pasting Block */}
      {viewMode === "plan" && selectedSlot && selectedMachine && (
        <Collapse in={pastingBlockVisible} timeout="auto" unmountOnExit>
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: "info.main" }}>
                  <CopyIcon color="#fff" size={20} />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">Excel Data Input</Typography>
              </Stack>
              <Alert severity="info" variant="outlined" icon={<Info color="inherit" size={20} />} sx={{ mb: 2 }}>
                <strong>Required:</strong> Job No:, WO description, Customer. <strong>Optional:</strong> All other fields including dates and time units.
              </Alert>
              <TextField
                fullWidth 
                multiline 
                minRows={6} 
                variant="outlined"
                placeholder="Paste your Excel table data here..."
                value={excelData} 
                onChange={handleExcelDataChange}
                sx={{ mb: 2, fontFamily: "monospace" }}
                helperText={
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      ✓ Paste data directly from Excel (Ctrl+C, then Ctrl+V here)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ✓ Required: Job No:, WO description, Customer
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ✓ Numeric fields: WO quantity, Op. quantity, Planned quantity
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ✓ All other fields (dates, time units) accepted as-is
                    </Typography>
                    {excelData && (
                      <Typography variant="caption" color="success.main" sx={{ fontWeight: "bold" }}>
                        ✓ {excelData.split('\n').length} lines pasted
                      </Typography>
                    )}
                  </Stack>
                }
              />
              <Stack direction="row" justifyContent="center" spacing={2}>
                <Button
                  variant="contained"
                  onClick={handlePasteExcelData}
                  startIcon={dataProcessing ? <CircularProgress size={16} color="inherit" /> : <CopyIcon size={18} />}
                  disabled={!excelData.trim() || dataProcessing}
                >
                  {dataProcessing ? "Processing..." : "Process Data"}
                </Button>
                <Button
                  variant="outlined" 
                  color="secondary"
                  onClick={handleClearPastedData}
                  startIcon={<XIcon size={18} />}
                  disabled={!excelData.trim() || dataProcessing}
                >
                  Clear Data
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Collapse>
      )}

      {/* Data Preview Table */}
      {viewMode === "plan" && data && data.length > 0 && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Avatar sx={{ bgcolor: "warning.main" }}>
                <Eye color="#fff" size={20} />
              </Avatar>
              <Typography variant="h6" fontWeight="bold">Data Preview</Typography>
              <Chip label={`${data.length - 1} Rows`} color="warning" variant="outlined" />
            </Stack>
            <TableContainer component={Paper} sx={{ maxHeight: 500, mb: 3, border: "1px solid #e0e0e0" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {data[0].map((headerCell, idx) => (
                      <TableCell key={idx} sx={{ fontWeight: "bold", backgroundColor: "warning.main", color: "#fff", whiteSpace: "nowrap" }}>
                        {headerCell}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.slice(1).map((row, rowIndex) => (
                    <TableRow key={rowIndex} hover>
                      {row.map((cell, cellIndex) => (
                        <TableCell key={cellIndex} sx={{ whiteSpace: "nowrap", borderRight: cellIndex === row.length - 1 ? "none" : "1px solid #e0e0e0" }}>
                          {cell !== null && cell !== "" ? cell : "-"}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack direction="row" justifyContent="center" spacing={2}>
              <Button
                variant="contained" 
                color="success"
                onClick={handleUploadPlan}
                startIcon={<UploadIcon size={20} />}
                disabled={loading || !data.length}
                sx={{ px: 4 }}
              >
                Confirm & Upload Plan
              </Button>
              <Button
                variant="outlined" 
                color="error"
                onClick={handleClearPreview}
                startIcon={<XIcon size={20} />}
                sx={{ px: 4 }}
              >
                Cancel Preview
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Simplified Data Summary Dialog */}
      <Dialog 
        open={showSummaryDialog} 
        onClose={() => setShowSummaryDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: "#e3f2fd" }}>
              <CheckCircle color="#4caf50" size={24} />
            </Avatar>
            <Box>
              <Typography variant="h6">Data Ready for Upload</Typography>
              <Typography variant="caption" color="text.secondary">
                {summaryType === "plan" ? "Production Plan" : "Machine Lineup"}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {(summaryType === "plan" ? dataSummary : lineupDataSummary) && (
            <Stack spacing={2}>
              {/* Quick Stats */}
              <Box sx={{ 
                display: "flex", 
                justifyContent: "space-around", 
                p: 2, 
                bgcolor: "#f5f5f5", 
                borderRadius: 2 
              }}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h3" color="primary" fontWeight="bold">
                    {(summaryType === "plan" ? dataSummary : lineupDataSummary).totalRows}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Jobs
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h3" color="success.main" fontWeight="bold">
                    {(summaryType === "plan" ? dataSummary : lineupDataSummary).fieldAnalysis.uniqueCustomers}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Customers
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h3" color="info.main" fontWeight="bold">
                    {(summaryType === "plan" ? dataSummary : lineupDataSummary).info[0]?.message.match(/\d+(\.\d+)?%/)?.[0] || "0%"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Complete
                  </Typography>
                </Box>
              </Box>

              {/* Warnings - Only if present */}
              {(summaryType === "plan" ? dataSummary : lineupDataSummary).warnings.length > 0 && (
                <Alert severity="warning" variant="outlined">
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    ⚠️ Attention Required ({(summaryType === "plan" ? dataSummary : lineupDataSummary).warnings.length})
                  </Typography>
                  {(summaryType === "plan" ? dataSummary : lineupDataSummary).warnings.slice(0, 3).map((warning, idx) => (
                    <Typography key={idx} variant="body2" sx={{ fontSize: "0.875rem", mt: 0.5 }}>
                      • {warning.message}
                    </Typography>
                  ))}
                  {(summaryType === "plan" ? dataSummary : lineupDataSummary).warnings.length > 3 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      +{(summaryType === "plan" ? dataSummary : lineupDataSummary).warnings.length - 3} more warnings
                    </Typography>
                  )}
                </Alert>
              )}

              {/* Key Statistics - Compact */}
              {Object.keys((summaryType === "plan" ? dataSummary : lineupDataSummary).numericStats).length > 0 && (
                <Card variant="outlined">
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      📊 Total Quantities
                    </Typography>
                    <Grid container spacing={1}>
                      {Object.entries((summaryType === "plan" ? dataSummary : lineupDataSummary).numericStats)
                        .filter(([field]) => field.includes("quantity") || field.includes("Qty"))
                        .map(([field, stats]) => (
                          <Grid item xs={6} key={field}>
                            <Box sx={{ p: 1, bgcolor: "#fafafa", borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {field.replace("quantity", "").replace("Qty", "").trim() || field}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {parseFloat(stats.sum).toLocaleString()}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* System Actions - Minimal */}
              <Box sx={{ p: 2, bgcolor: "#f0f7ff", borderRadius: 1, border: "1px solid #e3f2fd" }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">
                  ✅ Automatic Processing Complete
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontSize="0.8rem">
                    • Empty rows removed
                  </Typography>
                  <Typography variant="body2" fontSize="0.8rem">
                    • Numeric fields cleaned & converted to NULL if empty
                  </Typography>
                  <Typography variant="body2" fontSize="0.8rem">
                    • Required fields validated
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button 
            onClick={() => {
              setShowSummaryDialog(false);
              if (summaryType === "plan") {
                setData(null);
                setDataSummary(null);
                setPastingBlockVisible(true);
              } else {
                setLineupData(null);
                setLineupDataSummary(null);
                setLineupPasteBlockVisible(true);
              }
            }} 
            color="secondary"
            variant="outlined"
            startIcon={<XIcon size={16} />}
          >
            Edit Data
          </Button>
          <Button 
            onClick={() => setShowSummaryDialog(false)} 
            variant="contained"
            startIcon={<Eye size={16} />}
          >
            Continue to Preview
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validation Error Dialog */}
      <Dialog 
        open={showErrorDialog} 
        onClose={() => setShowErrorDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: "#ffebee" }}>
              <AlertTriangle color="#d32f2f" size={24} />
            </Avatar>
            <Typography variant="h6">Validation Errors ({validationErrors.length})</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Error Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {validationErrors.map((error, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Typography variant="body2" color="error">
                        {error}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrorDialog(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deletion Confirmation Dialogs */}
      <Dialog open={planConfirmOpen} onClose={() => setPlanConfirmOpen(false)}>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: "#ffebee" }}>
              <AlertTriangle color="#d32f2f" size={24} />
            </Avatar>
            <Typography variant="h6">Confirm Plan Deletion</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Are you sure you want to delete the existing plan? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanConfirmOpen(false)} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDeletePlan} color="error" variant="contained" startIcon={<Trash2 style={{ width: 18, height: 18 }} />}>
            Delete Plan
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={lineupConfirmOpen} onClose={() => setLineupConfirmOpen(false)}>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: "#ffebee" }}>
              <AlertTriangle color="#d32f2f" size={24} />
            </Avatar>
            <Typography variant="h6">Confirm Lineup Deletion</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Are you sure you want to delete the existing lineup? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLineupConfirmOpen(false)} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDeleteLineup} color="error" variant="contained" startIcon={<Trash2 style={{ width: 18, height: 18 }} />}>
            Delete Lineup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={handleSnackbarClose} severity={alertSeverity} variant="filled" sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>

      <PlanningHistoryDrawer jobNumber={historyJobNumber} onClose={() => setHistoryJobNumber(null)}
        onSelectJob={(number) => { setHistorySearch(number); setHistoryJobNumber(number); }} />

      {/* Footer */}
      <Box sx={{ py: 3, textAlign: "center", borderTop: "1px solid #e0e0e0" }}>
        <Typography variant="body2" color="text.secondary">
          Planning Upload System © 2025 | <MuiLink href="#" underline="hover" color="primary">Contact Support</MuiLink>
        </Typography>
      </Box>
    </Container>
  );
}